import PropTypes from 'prop-types';
import { Box, Grid, Typography } from '@mui/material';
import { useState, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTheme } from '@emotion/react';
import { Link, generatePath, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useQueryClient } from 'react-query';

// Our Components
import ColumnItem from 'components/DataTable/ColumnItem';
import DataTable from 'components/DataTable/DataTable';
import DataConfidence from 'components/SoraWallet/Cards/dataConfidence/DataConfidence';
import DeleteLiabilityModal from 'components/Modal/WalletOverview/DeleteLiabilityModal';
import ExportCsvButton from 'components/Button/Export/ExportCsvButton';
import { TertiaryButton } from 'components/Button/Button';
import TotalLiabilitiesActionButtons from 'components/SoraWallet/Cards/totalLiabilitiesCardComponents/TotalLiabilitiesActionButtons';

// Our Hooks
import { useIsFeatureFlagCompany } from 'shared/utils/featureflags/isFeatureFlagCompany';

// Our Utils
import {
	dataIsValid,
	dollarFormatter,
	getFormattedLiabilityRate,
	roundToDecimal
} from 'shared/utils';
import defaultLiabilitiesSortOrder from 'shared/utils/sorting/defaultLiabilitiesSortOrder';
import getLiabilityConfidence from 'shared/utils/calculations/getLiabilityConfidence';
import normalizeLoanType from 'shared/utils/clientOnboarding/normalizeLoanType';

// Routes
import {
	ADVISOR_NEW_LOAN_SELECTION_ROUTE,
	CLIENT_NEW_LOAN_SELECTION_ROUTE,
	ADVISOR_WALLET_LIABILITY,
	CLIENT_WALLET_LIABILITY
} from 'routes';

// Query Keys
import { USERPROFILEDATA } from 'shared/query-keys';

// Our Constants
import {
	CREDITCARD,
	MORTGAGE,
	SUPPORTED_LIABILITY_WALLETS
} from 'shared/constants';
import SoraToolTip from 'components/Tooltip/index';

// Local Constants
const CONFIDENCE_ORDER = {
	High: 1,
	Medium: 2,
	Low: 3
};

// Helper Functions
const getFormattedUpdatedDate = (updatedDate) => {
	if (updatedDate) {
		return format(new Date(updatedDate), 'MM/dd/yyyy');
	}

	return 'NA';
};

const dataConfidenceSortComparator = (confidenceOne, confidenceTwo) => {
	const orderOne = CONFIDENCE_ORDER[confidenceOne] ?? 4; // Default to 4 if not found
	const orderTwo = CONFIDENCE_ORDER[confidenceTwo] ?? 4; // Default to 4 if not found

	return orderOne - orderTwo;
};

function TotalLiabilitiesTable({
	advisor,
	bizLoans,
	epr,
	clientId,
	clientData,
	totalLiabilities,
	liabilities
}) {
	const SoraTheme = useTheme();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [liabilitySelected, setLiabilitySelected] = useState(null);

	const profileData = queryClient.getQueryData(USERPROFILEDATA);
	const isAdvisor = profileData?.role === 'ADVISOR';

	const isFeatureFlagCompany = useIsFeatureFlagCompany(advisor?.companyId);

	const toggleModal = (mode) => setIsModalOpen(mode ?? !isModalOpen);

	const handleClose = () => {
		toggleModal(false);
		setLiabilitySelected(null);
	};

	const path = isAdvisor
		? ADVISOR_NEW_LOAN_SELECTION_ROUTE
		: CLIENT_NEW_LOAN_SELECTION_ROUTE;

	const payload = isAdvisor ? { clientId } : {};

	const updateRoute = useMemo(() => generatePath(path, payload), [clientId]);

	const handleClick = (tradelineId) => {
		if (!isAdvisor) {
			navigate(`${CLIENT_WALLET_LIABILITY}${tradelineId}`);
			return;
		}

		// not a client
		navigate(
			`${ADVISOR_WALLET_LIABILITY}${clientId}/liability/${tradelineId}`
		);
	};

	const formattedTotalLiabilities = dollarFormatter.format(totalLiabilities);
	const formattedEpr = roundToDecimal(epr);

	const typeKey = 'tradeLineType';
	const detailsKey = 'lender';
	const rateKey = 'interestRate';
	const balanceKey = 'outstandingBalance';
	const monthlyPayKey = 'monthlyPay';
	const dataConfidenceKey = 'liabilityConfidence';
	const lastUpdatedKey = 'lastUpdatedDate';

	const totalLiabilitiesData = useMemo(() => {
		const liabilitiesAndBizLoan = [...liabilities, ...bizLoans].map(
			(userLiabilityData) => {
				const isMortgage = userLiabilityData.tradeLineType === MORTGAGE;
				if (isMortgage) {
					return {
						...userLiabilityData,
						monthlyPay: userLiabilityData.monthlyMortgagePayment,
						liabilityConfidence: getLiabilityConfidence(
							userLiabilityData.interestRateSource,
							userLiabilityData.rateManuallyUpdated,
							userLiabilityData.dataSource
						),
						id: uuidv4()
					};
				}

				return {
					...userLiabilityData,
					liabilityConfidence: getLiabilityConfidence(
						userLiabilityData.interestRateSource,
						userLiabilityData.rateManuallyUpdated,
						userLiabilityData.dataSource
					),
					id: uuidv4()
				};
			}
		);

		return defaultLiabilitiesSortOrder(liabilitiesAndBizLoan);
	}, [liabilities, bizLoans]);

	const totalLiabilitiesColumns = [
		ColumnItem(typeKey, 'Type', 1.1, {
			valueFormatter: (params) => {
				const currentTradelineType = params.value;
				if (currentTradelineType === CREDITCARD) return 'Credit Card';

				const formattedType = normalizeLoanType(currentTradelineType);
				return formattedType;
			}
		}),
		ColumnItem(detailsKey, 'Details', 1.1),
		ColumnItem(rateKey, 'Rate', 0.6, {
			valueFormatter: (params) => getFormattedLiabilityRate(params.value)
		}),
		ColumnItem(balanceKey, 'Balance', 1, {
			valueFormatter: (params) => dollarFormatter.format(params.value)
		}),
		ColumnItem(monthlyPayKey, 'Monthly P & I', 1, {
			valueFormatter: (params) => {
				if (!dataIsValid(params.value) || params.value === 0)
					return 'NA';
				return dollarFormatter.format(params.value);
			}
		}),
		ColumnItem(dataConfidenceKey, 'Data Confidence', 1, {
			renderCell: ({ value }) => <DataConfidence value={value} />,
			sortComparator: dataConfidenceSortComparator
		}),
		ColumnItem(lastUpdatedKey, 'Last Updated', 1, {
			valueFormatter: (params) => getFormattedUpdatedDate(params.value)
		}),
		ColumnItem('id', '', 0.6, {
			sortable: false,
			renderCell: ({ row }) => (
				<TotalLiabilitiesActionButtons
					liabilitySelectedData={row}
					handleDeleteClick={() => {
						setLiabilitySelected(row);
						toggleModal(true);
					}}
				/>
			)
		})
	];

	const sortingModelBasedOnFeatureFlag = isFeatureFlagCompany
		? [{ field: rateKey, sort: 'asc' }]
		: [];

	const exportableData = totalLiabilitiesData.map(
		({
			interestRate,
			lastUpdatedDate,
			lender,
			liabilityConfidence,
			monthlyPay,
			outstandingBalance,
			tradeLineType
		}) => ({
			type:
				tradeLineType === CREDITCARD
					? 'Credit Card'
					: normalizeLoanType(tradeLineType),
			details: lender,
			rate: `${interestRate}%`,
			balance: `${dollarFormatter.format(outstandingBalance)}`,
			monthlyPayment: `${dollarFormatter.format(monthlyPay)}`,
			dataConfidence: liabilityConfidence,
			lastUpdated: lastUpdatedDate
		})
	);

	return (
		<>
			<Grid
				container
				item
				xs={12}
				sx={{
					marginBottom: 2,
					justifyContent: 'space-between',
					alignItems: 'center'
				}}
			>
				<Box sx={{ display: 'flex', gap: 1 }}>
					<Typography
						variant="h2Gascogne"
						sx={{
							color: SoraTheme.palette.primary.indigo,
							display: 'block'
						}}
					>
						Total Liabilities: {formattedTotalLiabilities}
					</Typography>

					{isFeatureFlagCompany && (
						<>
							<Typography
								variant="h2Gascogne"
								sx={{
									color: SoraTheme.palette.primary.indigo,
									display: 'block'
								}}
							>
								EPR: {formattedEpr}%
							</Typography>
							<SoraToolTip
								text={
									<Box sx={{ textAlign: 'center' }}>
										<Typography>
											Weighted average interest rate
										</Typography>
										<Typography>
											Grey rows are above EPR
										</Typography>
									</Box>
								}
							/>
						</>
					)}
				</Box>

				<Box>
					<ExportCsvButton
						data={exportableData}
						fileName={`${clientData?.firstName}${clientData?.lastName}_liabilities.csv`}
					/>
					<Link
						to={updateRoute}
						state={{ fromWallet: true, ...clientId }}
					>
						<TertiaryButton>Add Liabilities</TertiaryButton>
					</Link>
				</Box>
			</Grid>

			<Grid item xs={12}>
				<DataTable
					disableSelectionOnClick
					columns={totalLiabilitiesColumns}
					rows={totalLiabilitiesData}
					withRowColorCheck
					getRowClassName={(params) => {
						const selectedTradelineType = params.row.tradeLineType;
						const isUnsupported =
							!SUPPORTED_LIABILITY_WALLETS.includes(
								selectedTradelineType
							);
						const liabilityRate = params.row.interestRate;
						const isHighRate = liabilityRate > epr;

						if (!isFeatureFlagCompany) return 'MuiDataGrid-row';

						if (isUnsupported && isHighRate) {
							return 'MuiDataGrid-row-special MuiDataGrid-row-no-cursor';
						}

						if (isUnsupported) {
							return 'MuiDataGrid-row-no-cursor';
						}

						if (isHighRate) {
							return 'MuiDataGrid-row-special';
						}

						return 'MuiDataGrid-row';
					}}
					rowsPerPageOptions={10}
					handleRowClick={({ tradelineId, tradeLineType }) => {
						if (
							!SUPPORTED_LIABILITY_WALLETS.includes(tradeLineType)
						)
							return;
						handleClick(tradelineId);
					}}
					sortingModel={sortingModelBasedOnFeatureFlag}
				/>
			</Grid>

			<DeleteLiabilityModal
				isModalOpen={isModalOpen}
				handleClose={handleClose}
				liabilitySelected={liabilitySelected}
			/>
		</>
	);
}

TotalLiabilitiesTable.propTypes = {
	totalLiabilities: PropTypes.number.isRequired
};

export default TotalLiabilitiesTable;
